import { ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { Button, Field, InlineError } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export function Login() {
  const { session, signIn } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (session.status === "signed-in") {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== "/login" ? from : "/"} replace />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 429
          ? new Error("Too many attempts. Wait a minute and try again.")
          : caught,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-full place-items-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <span className="grid size-9 place-items-center rounded-lg bg-accent">
            <ShieldCheck size={21} aria-hidden />
          </span>
          <span className="text-xl font-semibold">DroidManage</span>
        </div>
        <form onSubmit={submit} className="card space-y-4 p-6" noValidate>
          <h1 className="text-base font-semibold">Sign in</h1>
          <Field label="Email">
            {(props) => (
              <input
                {...props}
                className="field"
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
          <Field label="Password">
            {(props) => (
              <input
                {...props}
                className="field"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>
          <InlineError error={error} />
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            busy={busy}
            disabled={!email.trim() || !password}
          >
            Sign in
          </Button>
        </form>
        <p className="mt-4 text-center text-[13px] text-muted">
          Accounts are created by an administrator.
        </p>
      </div>
    </div>
  );
}
